import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostBinding,
  ViewEncapsulation,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationService } from '../../../services/confirmation.service';
import { OspDialogService } from '../../../services/osp-dialog.service';
import { OspDialogRef } from '../dialog/contract/osp-dialog-ref.contract';
import { OspDialogSize } from '../dialog/contract/osp-dialog-size.contract';
import { ConfirmDialogContentComponent } from './confirm-dialog-content/confirm-dialog-content.component';
import { Confirmation } from './contract/confirm-dialog-confirmation.contract';
import { ConfirmDialogResult } from './contract/confirm-dialog-result.contract';

const PANE_STYLE_CLASS = 'osp-confirm-dialog-pane';

@Component({
  selector: 'osp-confirm-dialog',
  standalone: true,
  templateUrl: './confirm-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ConfirmDialogComponent {
  @HostBinding('class.osp-confirm-dialog') protected readonly hostCssClassEnabled = true;

  readonly $key = input<string | undefined>(undefined, { alias: 'key' });

  private readonly confirmationService = inject(ConfirmationService);
  private readonly ospDialogService = inject(OspDialogService);
  private readonly destroyRef = inject(DestroyRef);

  private dialogRef: OspDialogRef<ConfirmDialogContentComponent, ConfirmDialogResult> | null = null;
  private closeSubscription: Subscription | null = null;

  constructor() {
    this.confirmationService.requireConfirmation$
      .pipe(takeUntilDestroyed())
      .subscribe((confirmation) => this.handleConfirmation(confirmation));
  }

  private handleConfirmation(confirmation: Confirmation | null): void {
    if (!confirmation) {
      this.closeCurrentDialog();
      return;
    }
    if (confirmation.key !== this.$key()) {
      return;
    }
    this.closeCurrentDialog();
    this.openDialog(confirmation);
  }

  private openDialog(confirmation: Confirmation): void {
    const dialogRef = this.ospDialogService.open<ConfirmDialogContentComponent, ConfirmDialogResult>(
      ConfirmDialogContentComponent,
      {
        header: confirmation.header,
        size: OspDialogSize.Confirmation,
        showHeader: true,
        closable: false,
        styleClass: PANE_STYLE_CLASS,
        data: confirmation,
      },
    );

    this.dialogRef = dialogRef;
    this.closeSubscription = dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.handleClose(confirmation, result));
  }

  private handleClose(confirmation: Confirmation, result: ConfirmDialogResult | undefined): void {
    this.dialogRef = null;
    this.closeSubscription = null;
    if (result === 'accept') {
      confirmation.accept();
      return;
    }
    confirmation.reject?.();
  }

  private closeCurrentDialog(): void {
    this.closeSubscription?.unsubscribe();
    this.closeSubscription = null;
    this.dialogRef?.close();
    this.dialogRef = null;
  }
}
